// Theme resolution for the standalone signup app.
//
// The wizard is embedded in an iframe on the ZynoTalk lander, which has a dark mode. A cross-origin
// frame inherits nothing from its host — not `[data-theme]`, not `color-scheme`, not a stylesheet —
// so a light-only wizard sat as a white sheet in the middle of a dark page. The host has to hand its
// theme across explicitly, and the wizard applies it to its own document.
//
// Two channels, because a theme can arrive at two different times:
//   - `?theme=light|dark` on the frame URL, for the theme the host is already in at load. It is read
//     twice: once by the inline script in `embedded/signup/index.html` (before first paint, so a
//     dark-mode reader never sees a flash of white) and once here, which is what makes this module
//     correct on its own — in the SPA, in a test, or if that script is ever dropped.
//   - a `zs:signup:theme` message, for the host's toggle being flipped after the frame is up.
//
// With neither, the document follows the OS: `signup.theme.css` declares `color-scheme: light dark`,
// which is the right default both standalone and under a host that is itself following the OS.

import { onBeforeUnmount, onMounted } from 'vue';

export type SignupTheme = 'light' | 'dark';

/** Host → embed. The payload is `{ type, theme }`, where a null/absent theme means "follow the OS". */
const THEME_MESSAGE_TYPE = 'zs:signup:theme';

type ThemeListener = (theme: SignupTheme) => void;

const listeners = new Set<ThemeListener>();

/**
 * Pin the document to a theme, or hand it back to the OS with `null`.
 *
 * The attribute is the same hook the design system's dark ramp keys off
 * (`:root[data-theme='dark'] { color-scheme: dark }`), and the same one the lander sets on itself —
 * so host and embed are pinned by identical means on both sides of the frame boundary.
 */
export function applyTheme(theme: SignupTheme | null): void {
    const root = document.documentElement;
    if (theme) root.setAttribute('data-theme', theme);
    else root.removeAttribute('data-theme');

    notify();
}

function notify(): void {
    const resolved = resolvedTheme();
    for (const listener of listeners) listener(resolved);
}

/** Which scheme the document is actually rendering — a pin if there is one, else the OS. */
export function resolvedTheme(): SignupTheme {
    const pinned = document.documentElement.getAttribute('data-theme');
    if (pinned === 'light' || pinned === 'dark') return pinned;
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/**
 * Subscribe to theme changes — for the parts of the wizard that CSS can't re-tone on its own,
 * namely Stripe's cross-origin card field (see `stripe.ts`). Returns an unsubscribe.
 */
export function onThemeChange(listener: ThemeListener): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
}

/**
 * The `theme` query parameter, wherever this surface keeps its query string.
 *
 * Mirrors `manager.ts`'s `currentSearch`: on its own deployment the wizard runs on web history
 * (`/talk?theme=dark`), but anywhere the entry is loaded by its in-project path — local dev, the e2e
 * build — it runs on hash history and the params ride in the hash instead.
 */
function themeFromUrl(): SignupTheme | null {
    const hashQueryStart = window.location.hash.indexOf('?');
    const search = hashQueryStart >= 0 ? window.location.hash.slice(hashQueryStart) : window.location.search;
    const value = new URLSearchParams(search).get('theme');
    return value === 'light' || value === 'dark' ? value : null;
}

/** A `zs:signup:theme` message from the host, or undefined if this isn't one. */
function themeFromMessage(event: MessageEvent): SignupTheme | null | undefined {
    // Only the framing page may re-theme this document. The message carries no secrets and does
    // nothing but flip an attribute, so there is no origin allowlist to check against — the embed
    // deliberately doesn't know which site framed it (see `embed.ts`) — but a message from some
    // other frame on the page has no business steering it either.
    if (event.source !== window.parent) return undefined;

    const data = event.data as { type?: unknown; theme?: unknown } | null;
    if (!data || data.type !== THEME_MESSAGE_TYPE) return undefined;

    return data.theme === 'light' || data.theme === 'dark' ? data.theme : null;
}

/**
 * Follow the host's theme for as long as this app is mounted. Standalone (no host, no `?theme=`)
 * this is inert and the document keeps following the OS.
 */
export function useEmbedTheme(): void {
    const systemScheme = window.matchMedia('(prefers-color-scheme: dark)');

    function onMessage(event: MessageEvent) {
        const theme = themeFromMessage(event);
        if (theme !== undefined) applyTheme(theme);
    }

    // Nothing to apply when the OS flips — the CSS is already following it — but anything that had to
    // be handed its colours (the card field) does need telling.
    function onSystemScheme() {
        if (!document.documentElement.hasAttribute('data-theme')) notify();
    }

    onMounted(() => {
        applyTheme(themeFromUrl());
        window.addEventListener('message', onMessage);
        systemScheme.addEventListener('change', onSystemScheme);
    });

    onBeforeUnmount(() => {
        window.removeEventListener('message', onMessage);
        systemScheme.removeEventListener('change', onSystemScheme);
    });
}

/**
 * The *used* value of a colour token, e.g. `resolveTokenColor('--surface')` → `'rgb(27, 36, 56)'`.
 *
 * `getComputedStyle(root).getPropertyValue('--surface')` is no use here: a custom property computes
 * to its token stream, so under the dark ramp it hands back the literal string
 * `light-dark(#ffffff, #1b2438)`. Painting the token onto a probe element and reading `color` back
 * makes the browser resolve it against the document's current scheme, which is what a third party
 * (Stripe) needs to be handed.
 */
export function resolveTokenColor(token: string): string {
    const probe = document.createElement('span');
    probe.style.display = 'none';
    probe.style.color = `var(${token})`;
    document.body.appendChild(probe);
    const value = getComputedStyle(probe).color;
    probe.remove();
    return value;
}

/**
 * Whether the surfaces this document paints are dark — read off a resolved token rather than
 * `prefers-color-scheme`, so it stays right on a surface that has no dark ramp at all. The SPA is
 * light-only, and a partner using the MSP order builder with a dark OS must still get a light card
 * field to match the light pane around it.
 */
export function isDarkSurface(): boolean {
    const [r, g, b] = (resolveTokenColor('--surface').match(/\d+(\.\d+)?/g) ?? ['255', '255', '255']).map(Number);
    return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255 < 0.5;
}
